import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { handleFetchRoundsHistory, handleFetchReplayInit } from "../../service";
// Cross-slice listen on the shared spin thunk (same pattern potCollectionSlice/
// featureGameSettingsSlice/userInterfaceSlice already use) — not this slice's
// own thunk, just reacting to its result. See the rgsPlay.fulfilled case below.
import { rgsPlay } from "./spinSlice";

/**
 * Replay Slice
 * Owns the data behind the Replay & History feature (see docs/replay-history-spec.md).
 *
 * Scope today: the rounds-history list shown in ReplayOverlay. Replay-session
 * state (step cursor, active playback) will be added here when that lands —
 * this slice is the single home for replay domain data.
 *
 * NOTE: panel visibility (`replayActive`) intentionally stays in
 * userInterfaceSlice with the other popup flags. This slice holds data only.
 *
 * Data source: PopiPlay `actions.history.link` → GET (see PopiPlayRGS.fetchRoundsHistory).
 *
 * State fields:
 * - historyStatus: "idle" | "loading" | "succeeded" | "failed" — drives the
 *   panel's spinner / empty / error states
 * - rounds: raw rows in server order (newest first). Amounts are in SUBUNITS
 *   and timestamps are raw ISO strings — components format at render, matching
 *   how state.game is consumed.
 * - historyError: message string when the request failed, else null
 * - enterRound / endedRound: the round currently shown in the Enter/Ended
 *   modals (same row shape as `rounds`). Two separate fields, not one shared
 *   "selected round" — during the Enter→Ended handoff both modals are
 *   briefly mounted at once (Enter fading out while Ended fades in), and a
 *   shared field would have one modal's close-cleanup wipe out data the
 *   other is still displaying mid-fade. Visibility (`replayEnterActive` /
 *   `replayEndedActive`) lives in userInterfaceSlice with the other popup
 *   flags — same split as `replayActive` above.
 * - sessionActive: true only while a replay round is actively playing —
 *   distinct from the panel-visibility flags above, which just track which
 *   popup is open. This is what the replay indicator badge (§5) and the
 *   bet/autoplay/buy-bonus restrictions (§9) will read once those land.
 * - stepIds / maxStepId: the replay round's step sequence, from
 *   fetchReplayInit (§0 Stage 1). stepIds is the ordered list to walk
 *   through (spec §6.2 precedence); maxStepId is only a fallback for when
 *   stepIds is absent. stepCursor is the index into stepIds of the NEXT
 *   step to fetch — selectNextReplayStepId below turns these into "what
 *   step id do we ask for next" (or null once the round is finished).
 * - replayBet / replayBalance: the round's own bet + wallet snapshot from
 *   fetchReplayInit, in subunits — informational display only. BalanceDisplay
 *   / BetDisplay swap to these while sessionActive is true instead of the
 *   live state.game values, which are never touched by replay at all — so
 *   "restoring" the live numbers on exit needs no code of its own, the
 *   display components just fall back to state.game the moment sessionActive
 *   goes false again.
 * - replayFinished: true once the RGS object's own step cursor (private to
 *   it — see RGS.js _nextReplayStepId) has run out of steps. Set from the
 *   spin result itself (rgsPlay.fulfilled's `replayFinished` field, added by
 *   _replaySpin) rather than computed from stepIds/stepCursor above, because
 *   those are never advanced from this side — the RGS object tracks its own
 *   copy so spin()'s replay branch doesn't need Redux access. This is the
 *   signal ReelControlContext.handleSpin checks to show the Ended modal
 *   instead of starting another spin.
 */

const initialState = {
  historyStatus: "idle",
  rounds: [],
  historyError: null,
  enterRound: null,
  endedRound: null,
  sessionActive: false,
  stepIds: null,
  maxStepId: null,
  stepCursor: 0,
  lastStepId: null,
  replayBet: null,
  replayBalance: null,
  replayLastAppliedWin: 0,
  // The replayed ROUND's own total win (balance.game, cumulative across its
  // steps). Tracked separately from replayLastAppliedWin because that one is a
  // credit ledger — free-rounds completion pushes the whole CAMPAIGN total
  // through it, which is not this round's win.
  replayRoundWin: 0,
  // The replayed round's own id (flow.round_id). Shown by the Ended modal in
  // place of a played-at date when there's no history row to date the round.
  replayRoundId: null,
  // Whether this playthrough has already taken its wallet from a step
  // response. The wallet is identical on every step of a round, so it must
  // be adopted once (see the rgsPlay.fulfilled case) — re-adopting would
  // undo wins already revealed by earlier steps.
  replayWalletAdopted: false,
  replayInitError: null,
  replayFinished: false,
  // True only for the standalone replay-link entry point (docs/replay-history-spec.md
  // §0 Stage 9) — a shared link opened directly, with no live game underneath.
  //
  // Balance handling does NOT branch on this: both entry points run the same
  // reveal (fetchReplayInit seeds the round's starting wallet, each step's
  // wallet applies the bet, applyReplayWin credits the win). What this flag
  // gates is the things that genuinely differ with no live game to return
  // to — never opening the History board, and the Ended modal's X
  // re-launching the round instead of exiting.
  //
  // For "is this page a standalone replay page" (a permanent fact that must
  // survive endReplaySession resetting this to false), read
  // launcherOptions.isStandaloneReplayMode() instead.
  isStandalone: false,
};

/**
 * Fetch the player's rounds history.
 *
 * Always refetched when the panel opens — never cached. The signed
 * `replayUrl` / `roundId` in each row are re-issued by the server on every
 * call, so a stale list would hand out dead replay links.
 */
export const fetchRoundsHistory = createAsyncThunk(
  "replay/fetchRoundsHistory",
  async (_, { rejectWithValue }) => {
    try {
      const rounds = await handleFetchRoundsHistory();
      return { rounds };
    } catch (error) {
      if (error?.errors && Array.isArray(error.errors)) {
        return rejectWithValue(error);
      }
      return rejectWithValue(error.message);
    }
  },
);

/**
 * Fetch the replay round's step sequence (docs/replay-history-spec.md §0
 * Stage 1). Dispatched right after enterReplayMode() from the Enter modal's
 * Play button — must run after that call has resolved (needs this.replayUrl
 * already set on the RGS object).
 */
export const fetchReplayInit = createAsyncThunk(
  "replay/fetchReplayInit",
  async (_, { rejectWithValue }) => {
    try {
      return await handleFetchReplayInit();
    } catch (error) {
      if (error?.errors && Array.isArray(error.errors)) {
        return rejectWithValue(error);
      }
      return rejectWithValue(error.message);
    }
  },
);

const replaySlice = createSlice({
  name: "replay",
  initialState,
  reducers: {
    /**
     * Drop the cached list and reset to idle.
     * Used when the panel closes so the next open always refetches.
     */
    clearRoundsHistory(state) {
      state.historyStatus = "idle";
      state.rounds = [];
      state.historyError = null;
    },

    /** Enter modal — set on a history row's Play click, cleared once its own close-fade finishes. */
    setEnterRound(state, action) {
      state.enterRound = action.payload;
    },
    clearEnterRound(state) {
      state.enterRound = null;
    },

    /** Ended modal — set once a replay finishes, cleared once its own close-fade finishes. */
    setEndedRound(state, action) {
      state.endedRound = action.payload;
    },
    clearEndedRound(state) {
      state.endedRound = null;
    },

    /**
     * Replay session engine (docs/replay-history-spec.md §0). startReplaySession
     * is dispatched from the Enter modal's Play button — set on entry, cleared
     * once the player exits back to the live game. Pass `{standalone: true}`
     * only from the standalone replay-link boot path (Stage 9) — see
     * `isStandalone`'s comment on initialState above.
     */
    startReplaySession(state, action) {
      state.sessionActive = true;
      state.replayFinished = false;
      state.isStandalone = action?.payload?.standalone === true;
    },
    /**
     * Adds one step's win back onto the replay-only display balance, at the
     * exact moment (win animation finished) the live game would sync its
     * real balance — see WinAnimationSecondaryController.finishAnimation().
     * Never touches state.game.balance; purely the number BalanceDisplay
     * shows while sessionActive.
     *
     * The payload is currentRound.win, which RGS.js's own _replaySpin maps
     * from the step response's balance.game — per RGS.js's live-path
     * comment, that value ACCUMULATES across bonus/respin steps rather than
     * resetting per step (outcome.win resets to 0, balance.game doesn't).
     * Adding it raw on every step would double-count for any multi-step
     * bonus round, so this tracks what was already applied and only adds
     * the difference — correct for both a plain single-step round (delta
     * == the one value) and a multi-step one (delta == that step's actual
     * contribution).
     */
    applyReplayWin(state, action) {
      const cumulativeWin = action.payload || 0;
      const delta = cumulativeWin - (state.replayLastAppliedWin || 0);
      state.replayLastAppliedWin = cumulativeWin;
      if (delta <= 0) return;
      state.replayBalance = Math.max(0, (state.replayBalance || 0) + delta);
    },

    /** Also clears the step cursor — the next replay session always starts fresh. */
    endReplaySession(state) {
      state.sessionActive = false;
      state.stepIds = null;
      state.maxStepId = null;
      state.stepCursor = 0;
      state.lastStepId = null;
      state.replayBet = null;
      state.replayBalance = null;
      state.replayLastAppliedWin = 0;
      state.replayRoundWin = 0;
      state.replayRoundId = null;
      state.replayWalletAdopted = false;
      state.replayInitError = null;
      state.replayFinished = false;
      state.isStandalone = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoundsHistory.pending, (state) => {
        state.historyStatus = "loading";
        state.historyError = null;
      })
      .addCase(fetchRoundsHistory.fulfilled, (state, action) => {
        state.historyStatus = "succeeded";
        state.rounds = action.payload.rounds || [];
        state.historyError = null;
      })
      .addCase(fetchRoundsHistory.rejected, (state, action) => {
        state.historyStatus = "failed";
        state.rounds = [];
        state.historyError =
          typeof action.payload === "string"
            ? action.payload
            : action.payload?.errors?.[0]?.desc || "Request failed";
      })
      .addCase(fetchReplayInit.pending, (state) => {
        state.replayInitError = null;
      })
      .addCase(fetchReplayInit.fulfilled, (state, action) => {
        const { stepIds, maxStepId, bet, balance } = action.payload;
        state.stepIds = stepIds;
        state.maxStepId = maxStepId;
        state.stepCursor = 0;
        state.lastStepId = null;
        // Fresh round (or "Replay Again" of the same one) — nothing applied
        // yet, and this playthrough hasn't taken its wallet from a step.
        state.replayLastAppliedWin = 0;
        state.replayRoundWin = 0;
        state.replayRoundId = null;
        state.replayWalletAdopted = false;
        state.replayInitError = null;
        state.replayBet = bet;
        // The init response's wallet IS the round's starting balance —
        // verified against the live API (2026-08-10): init reports
        // wallet:48929/game:0 and its step reports wallet:48679 (= 48929
        // − the 250 bet) with game:400. So the server already hands us the
        // exact pre-round number; nothing needs deriving from it.
        //
        // Each spin then overwrites this with that step's own wallet (see
        // the rgsPlay.fulfilled case below) and the win is credited on top
        // at reveal time, reproducing the original bet→win sequence. This
        // line is what resets it for every new playthrough.
        state.replayBalance = balance ?? 0;
      })
      .addCase(fetchReplayInit.rejected, (state, action) => {
        state.replayInitError =
          typeof action.payload === "string"
            ? action.payload
            : action.payload?.errors?.[0]?.desc || "Request failed";
      })
      // Every live spin also goes through rgsPlay.fulfilled — guard on
      // sessionActive so this never reacts to an unrelated live result
      // (whose payload has no replayFinished field anyway, but being
      // explicit here is cheap and reads clearly).
      .addCase(rgsPlay.fulfilled, (state, action) => {
        if (!state.sessionActive) return;
        state.replayFinished = action.payload?.replayFinished === true;

        // Adopt the server's own wallet for this round — but only ONCE per
        // playthrough, on its first step. Verified live (2026-08-10): the
        // wallet is the round's balance with the bet already deducted and
        // no win credited, and it stays at that same value on every
        // subsequent step (wins accrue in `balance.game`, not here).
        // Re-adopting it on each step of a multi-step bonus round would
        // therefore wipe the win already revealed by earlier steps and
        // visibly snap the balance backwards before re-crediting it.
        //
        // Taking it once reproduces the bet deduction the live game shows
        // at spin time, and applyReplayWin's cumulative delta tracking
        // (replayLastAppliedWin, deliberately NOT reset here) credits each
        // step's share on reveal, landing on the round's true final total.
        // This round's own win, straight from the step (balance.game).
        state.replayRoundWin = action.payload?.round?.win ?? state.replayRoundWin;
        state.replayRoundId = action.payload?.round?.id ?? state.replayRoundId;

        const stepWallet = action.payload?.replayWallet;
        if (typeof stepWallet === "number" && !state.replayWalletAdopted) {
          state.replayWalletAdopted = true;
          state.replayBalance = stepWallet;
        }
      });
  },
});

// ==============================
// 🔹 Selectors
// ==============================

export const selectReplayRounds = (state) => state.replay.rounds;

export const selectReplayHistoryStatus = (state) => state.replay.historyStatus;

export const selectReplayHistoryError = (state) => state.replay.historyError;

export const selectReplayHistoryLoading = (state) =>
  state.replay.historyStatus === "loading";

// True only once a successful fetch returned zero rounds — distinct from the
// idle/loading states so the panel can show "no rounds" without flashing it.
export const selectReplayHistoryEmpty = (state) =>
  state.replay.historyStatus === "succeeded" && state.replay.rounds.length === 0;

export const selectEnterRound = (state) => state.replay.enterRound;

export const selectEndedRound = (state) => state.replay.endedRound;

export const selectReplaySessionActive = (state) => state.replay.sessionActive;

// True only for the standalone replay-link entry point (§0 Stage 9) — see
// initialState's comment above for why balance-display/reveal logic needs
// to check this separately from selectReplaySessionActive.
export const selectIsStandaloneReplay = (state) => state.replay.isStandalone;

export const selectReplayInitError = (state) => state.replay.replayInitError;

export const selectReplayBet = (state) => state.replay.replayBet;

export const selectReplayBalance = (state) => state.replay.replayBalance;

export const selectReplayFinished = (state) => state.replay.replayFinished;

/** The replayed round's own id (flow.round_id) — Ended modal's date fallback. */
export const selectReplayRoundId = (state) => state.replay.replayRoundId;

/**
 * The step id to fetch next, or null once the round has no more steps
 * (time to show the Ended modal). Implements spec §6.2's precedence order:
 * 1. stepIds[] present → the entry at stepCursor, or null past the end.
 * 2. Otherwise, lastStepId < maxStepId → lastStepId + 1.
 * 3. Fallback → 1 (first call) or null (already made one call with nothing
 *    else to go on).
 */
export const selectNextReplayStepId = (state) => {
  const { stepIds, stepCursor, maxStepId, lastStepId } = state.replay;

  if (Array.isArray(stepIds)) {
    return stepCursor < stepIds.length ? stepIds[stepCursor] : null;
  }
  if (typeof maxStepId === "number" && typeof lastStepId === "number") {
    return lastStepId < maxStepId ? lastStepId + 1 : null;
  }
  return lastStepId == null ? 1 : null;
};

// ==============================
// 🔹 Export Actions
// ==============================

export const {
  clearRoundsHistory,
  setEnterRound,
  clearEnterRound,
  setEndedRound,
  clearEndedRound,
  startReplaySession,
  applyReplayWin,
  endReplaySession,
} = replaySlice.actions;

export default replaySlice.reducer;
